'use client';

import { useLocalStorage } from '@mantine/hooks';
import { NotificationData, notifications } from '@mantine/notifications';
import { MessagePayload } from 'firebase/messaging';
import localforage from 'localforage';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import useRollbar from '@/hooks/use-rollbar';
import { useAuth } from '@/providers/auth-provider/auth-provider';
import { getFirebaseMessaging, initMessaging } from '@/utils/firebase';
import { isMobile, isPWA } from '@/utils/platform';

export function useFirebaseMessaging() {
  const rollbar = useRollbar();
  const router = useRouter();
  const [token, setToken] = useState<string | null>(null);
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>('default');
  const [supported, setSupported] = useLocalStorage<boolean | null>({
    key: 'FCM_SUPPORTED',
    defaultValue: null,
  });

  useEffect(() => {
    if (typeof window !== 'undefined' && isSupported()) {
      setPermissionStatus(Notification.permission);
    }
  }, []);

  const { user } = useAuth();

  async function requestPermission() {
    if (!supported) return;
    importFirebaseMessaging()
      .then(async (messaging) => {
        if (!messaging) return;
        if ('Notification' in window && (await messaging.isSupported())) {
          const permission = await Notification.requestPermission();
          setPermissionStatus(permission);
        }
      })
      .catch((error) => {
        rollbar.error('useFirebaseMessaging - Falha ao importar firebase messaging', error);
      });
  }

  useEffect(() => {
    importFirebaseMessaging()
      .then((messaging) => {
        if (!messaging) {
          // Browser without the messaging
          setSupported(false);
          return;
        }
        messaging
          .isSupported()
          .then(setSupported)
          .catch((error) => rollbar.error('Falha ao verificar suporte a notificações', error));
      })
      .catch((error) =>
        rollbar.error('useFirebaseMessaging - Falha ao importar firebase messaging', error),
      );
  }, []);

  useEffect(() => {
    if (permissionStatus === 'granted' && supported) {
      initMessaging()
        .then((token) => {
          setToken(token || null);
        })
        .catch((error) => {
          rollbar.error('Falha ao inicializar firebase cloud messaging', error);
        });
    } else {
      localforage.removeItem('FCM_TOKEN');
    }
  }, [permissionStatus, supported]);

  useEffect(() => {
    if (permissionStatus === 'granted' && supported && user) {
      importFirebaseMessaging()
        .then(async (messaging) => {
          if (!messaging) {
            if (!isMobile.iOS()) rollbar.warn('Messaging returning undefined', { messaging });
            return;
          }
          const firebaseMessaging = await getFirebaseMessaging();
          if (!firebaseMessaging) {
            return;
          }

          try {
            const unsubscribe = messaging.onMessage(firebaseMessaging, (payload) => {
              // Avoid showing notification if the tab is not focused
              if (!document.hasFocus()) {
                navigator.serviceWorker.ready.then((registration) => {
                  registration.active?.postMessage({
                    messageType: 'background-notification',
                    payload,
                  });
                });
                return;
              }

              const id = notifications.show(
                generateNotificationConfig(payload, (destination) => {
                  router.push(destination);
                  notifications.hide(id);
                }),
              );
            });

            return () => {
              unsubscribe();
            };
          } catch (error) {
            rollbar.error('Error on onMessage listener', error as Error);
          }
        })
        .catch((error) => {
          rollbar.error('Error on importing Firebase Messaging', error);
        });
    }
  }, [permissionStatus, supported, user]);

  useEffect(() => {
    if (isMobile.iOS() && !isPWA()) return; // Notification is not available on iOS without the PWA installed

    const handleFirebaseMessage = (event: MessageEvent) => {
      try {
        if (event.data.messageType === 'notification-clicked') {
          const payload: MessagePayload = event.data;
          if (payload.data?.link) {
            router.push(payload.data.link);
          }
        }
      } catch (error) {
        rollbar.error('Error on handling Firebase notification clicked event', error as Error);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleFirebaseMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleFirebaseMessage);
  }, [router]);

  return [requestPermission, { permissionStatus, token, supported }] as const;
}

export async function importFirebaseMessaging() {
  // Firebase messaging can only be imported on the client side
  if (typeof window === 'undefined') return;

  // Firebase messaging can only be imported on supported browsers
  if (!isSupported()) return;

  const messaging = await import('firebase/messaging');
  return messaging;
}

export function isSupported() {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

export const generateNotificationConfig = (
  payload: MessagePayload,
  onClick: (url: string) => void,
): NotificationData => {
  return {
    title: payload.notification?.title,
    message: payload.notification?.body,
    autoClose: false,
    onClick: (event) => {
      // Checking if user clicked on the close button
      if (!(event.target as HTMLElement).closest('.mantine-Notification-closeButton')) {
        onClick(payload.data?.link || '/');
      }
    },
    classNames: {
      root: 'text-white cursor-pointer bg-secondary-900 hover:bg-secondary-700 transition-colors',
      title: 'text-white font-bold',
      description: 'text-white',
      closeButton: 'text-white hover:bg-gray-50 hover:text-secondary-900 transition-colors',
    },
  };
};
